<template>
  <div class="stock-count-page">

    <!-- Header fijo -->
    <div class="stock-count-header sticky-top bg-dark text-white p-3">
      <b-row align-v="center">
        <b-col cols="2">
          <b-button variant="link" @click="goBack()" class="text-white p-0">
            <b-icon icon="arrow-left" font-scale="1.5"></b-icon>
          </b-button>
        </b-col>
        <b-col cols="8" class="text-center">
          <h5 class="mb-0">Recuento de Stock</h5>
          <small class="text-muted">Visualización y ajustes de stock por depósito</small>
        </b-col>
        <b-col cols="2" class="text-right">
          <b-button
            variant="link"
            @click="toggleFocusMode()"
            class="text-white p-0"
            :title="focusMode ? 'Salir de pantalla completa' : 'Usar pantalla completa'"
          >
            <b-icon :icon="focusMode ? 'arrows-collapse' : 'arrows-fullscreen'" font-scale="1.2"></b-icon>
          </b-button>
        </b-col>
      </b-row>
    </div>

    <div class="p-3">

      <!-- Búsqueda por código de barras -->
      <div v-if="!product">
        <b-card class="mb-3 shadow-sm">
          <div class="text-center mb-3">
            <b-icon icon="upc-scan" font-scale="2.5" class="text-primary"></b-icon>
            <p class="mt-2 mb-0 text-muted small">Ingresá o escaneá el código de barras del producto</p>
          </div>
          <b-input-group>
            <b-form-input
              ref="barcodeInput"
              v-model="barcode"
              type="text"
              placeholder="Código de barras..."
              size="lg"
              autofocus
              @keyup.enter="searchProduct()"
              :disabled="loading.search"
            ></b-form-input>
            <b-input-group-append>
              <b-button
                variant="outline-secondary"
                size="lg"
                @click="startCamera()"
                :disabled="loading.search"
                title="Escanear con cámara"
              >
                <b-icon icon="camera"></b-icon>
              </b-button>
              <b-button
                variant="primary"
                size="lg"
                @click="searchProduct()"
                :disabled="!barcode || loading.search"
              >
                <b-icon icon="search" v-if="!loading.search"></b-icon>
                <b-spinner small v-else></b-spinner>
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <div v-if="searchError" class="mt-2">
            <b-alert variant="danger" show class="mb-0 py-2">
              <b-icon icon="exclamation-circle"></b-icon> {{ searchError }}
            </b-alert>
          </div>
        </b-card>

        <div class="text-center text-muted py-4">
          <b-icon icon="box-seam" font-scale="3" class="mb-2 d-block mx-auto"></b-icon>
          <p class="small mb-0">El sistema calculará los ajustes automáticamente</p>
          <p class="small">comparando el stock actual con el real ingresado</p>
        </div>
      </div>

      <!-- Resultado: producto + stock por depósito -->
      <div v-if="product">

        <!-- Card del producto -->
        <b-card class="mb-3 shadow-sm border-primary">
          <div class="d-flex align-items-start justify-content-between">
            <div class="flex-grow-1 mr-3">
              <h6 class="mb-1 font-weight-bold">{{ product.name }}</h6>
              <div class="text-muted small">
                <span v-if="product.code">Código: <strong>{{ product.code }}</strong></span>
                <span v-if="product.barcode" class="ml-2">Código de Barras: <strong>{{ product.barcode }}</strong></span>
              </div>
            </div>
            <div class="d-flex align-items-center">
              <b-button
                variant="outline-secondary"
                size="sm"
                class="stock-count-reset-btn flex-shrink-0"
                @click="resetSearch()"
                title="Buscar otro"
              >
                <b-icon icon="upc-scan" class="mr-1"></b-icon>
                Buscar otro
              </b-button>
            </div>
          </div>
        </b-card>

        <!-- Sin cambios -->
        <div v-if="product && changedCount === 0" class="stock-count-empty-state mt-3 mb-3">
          <b-icon icon="clipboard-check" class="stock-count-empty-icon"></b-icon>
          <div>
            <div class="stock-count-empty-title">Cargá el stock real del recuento</div>
            <div class="stock-count-empty-text">
              Ingresá la cantidad real de cada depósito. Si hay diferencias, el sistema prepara automáticamente los ajustes de ingreso o baja.
            </div>
          </div>
        </div>

        <b-card v-if="unitMeasurementOptions.length > 1" class="mb-3 shadow-sm">
          <b-form-group label="Ingresar cantidades en" label-class="small text-muted mb-1" class="mb-0">
            <b-form-select
              v-model="selectedUnitMeasurementId"
              :options="unitMeasurementOptions"
              :disabled="unitMeasurementOptions.length <= 1 || changedCount > 0"
              @change="onUnitMeasurementChange()"
            ></b-form-select>
          </b-form-group>
        </b-card>

        <!-- Stock por depósito -->
        <h6 class="text-uppercase text-muted mb-2" style="font-size:1rem;letter-spacing:1px;">
          <b-icon icon="building"></b-icon> Stock por depósito          
        </h6>

        <div v-if="stockByDeposit.length === 0" class="text-center text-muted py-3">
          <p class="small">No hay depósitos activos configurados</p>
        </div>

        <b-card
          v-for="(item, index) in stockByDeposit"
          :key="item.deposit.id"
          class="mb-2 shadow-sm"
          :class="{ 'border-warning': hasChanged(item), 'border-success': item.real_quantity > item.current_quantity && hasChanged(item), 'border-danger': item.real_quantity < item.current_quantity && hasChanged(item) }"
        >
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong class="text-dark">{{ item.deposit.name }}</strong>
            <span class="text-muted small">
              Stock actual: <strong>{{ formatQty(item.current_quantity) }}</strong>
              <span v-if="selectedUnitMeasurement && selectedUnitMeasurement.reference"> {{ selectedUnitMeasurement.reference }}</span>
            </span>
          </div>

          <b-input-group size="sm">
            <b-input-group-prepend is-text>
              <span class="small text-muted">Stock Real</span>
            </b-input-group-prepend>
            <b-form-input
              v-model.number="stockByDeposit[index].real_quantity"
              type="number"
              step="any"
              min="0"
              :class="getInputClass(item)"
              @input="onQuantityChange(item)"
            ></b-form-input>
            <b-input-group-append v-if="selectedUnitMeasurement && selectedUnitMeasurement.reference" is-text>
              <span class="small text-muted">{{ selectedUnitMeasurement.reference }}</span>
            </b-input-group-append>
          </b-input-group>

          <div v-if="hasChanged(item)" class="mt-1">
            <small :class="getDiffClass(item)">
              <b-icon :icon="item.real_quantity > item.current_quantity ? 'arrow-up' : 'arrow-down'"></b-icon>
              {{ item.real_quantity > item.current_quantity ? 'Ingreso' : 'Baja' }}:
              {{ formatQty(Math.abs(item.real_quantity - item.current_quantity)) }}
              ({{ item.real_quantity > item.current_quantity ? 'add-manual' : 'sub-manual' }})
            </small>
          </div>
        </b-card>

        <b-card v-if="stockByDeposit.length > 1" class="stock-count-total-card mt-3 mb-3">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div class="stock-count-total-title">Total del recuento</div>
              <div class="stock-count-total-subtitle">Suma de todos los depósitos</div>
            </div>
            <b-badge v-if="selectedUnitMeasurement && selectedUnitMeasurement.reference" variant="light" class="stock-count-total-unit">
              {{ selectedUnitMeasurement.reference }}
            </b-badge>
          </div>

          <div class="stock-count-total-grid">
            <div class="stock-count-total-box">
              <div class="stock-count-total-label">Stock del sistema</div>
              <div class="stock-count-total-value">{{ formatQty(totalCurrentQuantity) }}</div>
            </div>
            <div class="stock-count-total-box stock-count-total-box-primary">
              <div class="stock-count-total-label">Stock recuento</div>
              <div class="stock-count-total-value">{{ formatQty(totalRealQuantity) }}</div>
            </div>
          </div>

          <div v-if="hasTotalChanged" class="stock-count-total-diff" :class="totalDiffQuantity > 0 ? 'text-success' : 'text-danger'">
            <b-icon :icon="totalDiffQuantity > 0 ? 'arrow-up' : 'arrow-down'"></b-icon>
            Diferencia total: {{ formatQty(Math.abs(totalDiffQuantity)) }}
          </div>
        </b-card>

        <!-- Botón procesar -->
        <b-button
          block
          variant="success"
          size="lg"
          class="mt-3"
          :disabled="changedCount === 0 || loading.process"
          @click="processAdjustments()"
        >
          <b-spinner small v-if="loading.process" class="mr-1"></b-spinner>
          <b-icon icon="check2-circle" v-else class="mr-1"></b-icon>
          Procesar Ajustes ({{ changedCount }})
        </b-button>

      </div>

    </div>

    <!-- Modal cámara -->
    <b-modal
      v-model="modal.camera"
      title="Escanear código"
      hide-footer
      centered
      @hide="stopCamera()"
    >
      <div class="text-center">
        <video ref="videoElement" autoplay playsinline style="width:100%;border-radius:8px;max-height:60vh;background:#000;"></video>
        <p class="text-muted small mt-2 mb-0">Apuntá la cámara al código de barras</p>
        <p class="text-muted small" v-if="!cameraSupported">Tu navegador no soporta el escáner nativo — escribí el código manualmente</p>
      </div>
    </b-modal>

    <!-- Modal éxito -->
    <b-modal
      v-model="modal.success"
      title="Ajustes procesados"
      ok-only
      ok-title="Nuevo Recuento"
      ok-variant="primary"
      @ok="resetSearch()"
      centered
    >
      <div class="text-center py-2">
        <b-icon icon="check-circle" font-scale="3" class="text-success mb-3"></b-icon>
        <p class="mb-1">Se procesaron <strong>{{ lastResult.operations_created }}</strong> operaciones de ajuste</p>
        <p class="text-muted small mb-0">El stock ha sido actualizado correctamente</p>
      </div>
    </b-modal>

  </div>
</template>

<script>
import serviceAPI from './services'
import Error from '@/handler/error'
import Profiles from '@/config/profiles'
import Modules from '@/config/modules'
import Helper from '@/handler/helper'
import Storage from '@/handler/storageLocal'

export default {
  data: () => {
    return {
      access: {
        module_id: Modules.DEPOSITOS,
        profile_id: Profiles.PERSONAL,
        view_reference: 'crudDepositsStockCount',
        elements: {}
      },
      barcode: '',
      product: null,
      unitMeasurement: null,
      unitMeasurements: [],
      selectedUnitMeasurementId: null,
      stockByDeposit: [],
      searchError: null,
      loading: {
        search: false,
        process: false,
      },
      modal: {
        success: false,
        camera: false,
      },
      cameraStream: null,
      cameraDetector: null,
      cameraInterval: null,
      cameraSupported: true,
      focusMode: false,
      lastResult: {
        operations_created: 0,
      },
    }
  },
  computed: {
    changedCount() {
      return this.stockByDeposit.filter(item => this.hasChanged(item)).length
    },
    totalCurrentQuantity() {
      return this.stockByDeposit.reduce((total, item) => total + this.toNumber(item.current_quantity), 0)
    },
    totalRealQuantity() {
      return this.stockByDeposit.reduce((total, item) => total + this.toNumber(item.real_quantity), 0)
    },
    totalDiffQuantity() {
      return this.totalRealQuantity - this.totalCurrentQuantity
    },
    hasTotalChanged() {
      return Math.abs(this.totalDiffQuantity) >= 0.00005
    },
    selectedUnitMeasurement() {
      return this.unitMeasurements.find(item => item.id == this.selectedUnitMeasurementId) || this.unitMeasurement
    },
    unitMeasurementOptions() {
      return this.unitMeasurements.map(item => ({
        value: item.id,
        text: this.getUnitMeasurementOptionText(item)
      }))
    }
  },
  watch: {
    barcode(val) {
      if (!val) return
      clearTimeout(this._barcodeTimer)
      this._barcodeTimer = setTimeout(() => {
        if (this.barcode && !this.product && !this.loading.search) {
          this.searchProduct()
        }
      }, 300)
    }
  },
  created() {
    Helper.hasAccessView(this.access)
  },
  beforeDestroy() {
    this.stopCamera()
    this.disableFocusMode()
  },
  mounted() {
    this.disableFocusMode()
    this.$nextTick(() => {
      if (this.$refs.barcodeInput) {
        this.$refs.barcodeInput.$el.focus()
      }
    })
  },
  methods: {
    goBack() {
      this.exitBrowserFullscreen()
      this.disableFocusMode()
      this.$router.push({ name: 'DepositsStaffDashboard' })
    },
    async toggleFocusMode() {
      const enable = !this.focusMode
      if (enable) {
        this.enableFocusMode()
        await this.requestBrowserFullscreen()
      } else {
        await this.exitBrowserFullscreen()
        this.disableFocusMode()
      }
    },
    enableFocusMode() {
      this.focusMode = true
      if (typeof document === 'undefined') return
      if (document.body) {
        document.body.classList.add('stock-count-focus-mode')
      }
    },
    disableFocusMode() {
      this.focusMode = false
      if (typeof document === 'undefined') return
      if (document.body) {
        document.body.classList.remove('stock-count-focus-mode')
      }
    },
    async requestBrowserFullscreen() {
      if (typeof document === 'undefined') return
      if (document.fullscreenElement) return
      const target = document.documentElement
      if (target && typeof target.requestFullscreen === 'function') {
        try {
          await target.requestFullscreen()
        } catch (error) {
          if (error) return false
        }
      }
    },
    async exitBrowserFullscreen() {
      if (typeof document === 'undefined') return
      if (!document.fullscreenElement) return
      if (typeof document.exitFullscreen === 'function') {
        try {
          await document.exitFullscreen()
        } catch (error) {
          if (error) return false
        }
      }
    },
    resetSearch() {
      this.barcode = ''
      this.product = null
      this.unitMeasurement = null
      this.unitMeasurements = []
      this.selectedUnitMeasurementId = null
      this.stockByDeposit = []
      this.searchError = null
      this.$nextTick(() => {
        if (this.$refs.barcodeInput) {
          this.$refs.barcodeInput.$el.focus()
        }
      })
    },
    searchProduct() {
      if (!this.barcode) return
      this.loading.search = true
      this.searchError = null

      serviceAPI.buscarPorCodigoBarras({ barcode: this.barcode })
        .then((response) => {
          this.product = response.data.product
          this.unitMeasurement = response.data.unit_measurement
          this.unitMeasurements = response.data.unit_measurements || (this.unitMeasurement ? [this.unitMeasurement] : [])
          this.selectedUnitMeasurementId = this.getPreferredUnitMeasurementId()
          this.stockByDeposit = response.data.stock_by_deposit
          this.applySelectedUnitMeasurement()
          this.loading.search = false
        })
        .catch((error) => {
          this.searchError = Error.showError(error)
          this.loading.search = false
        })
    },
    hasChanged(item) {
      return Math.abs((item.real_quantity || 0) - item.current_quantity) >= 0.00005
    },
    toNumber(value) {
      var number = parseFloat(value)
      return isNaN(number) ? 0 : number
    },
    onQuantityChange() {
      // reactive trigger for computed
    },
    getUnitMeasurementOptionText(item) {
      var reference = item.reference || item.name || 'Unidad'
      var type = item.type === 'sale' ? 'venta' : 'compra'
      return reference + ' - ' + type
    },
    getPreferredUnitMeasurementId() {
      var preferredType = Storage.getValue('deposits_stock_count_unit_type')
      var preferred = this.unitMeasurements.find(item => item.type === preferredType)
      if (preferred) return preferred.id
      return this.unitMeasurement ? this.unitMeasurement.id : null
    },
    onUnitMeasurementChange() {
      var selected = this.selectedUnitMeasurement
      if (selected && selected.type) {
        Storage.setValue('deposits_stock_count_unit_type', selected.type)
      }
      this.applySelectedUnitMeasurement()
    },
    applySelectedUnitMeasurement() {
      var selected = this.selectedUnitMeasurement
      if (!selected) return
      this.stockByDeposit = this.stockByDeposit.map(item => {
        var current = item.current_quantity
        if (selected.type === 'sale' && item.current_quantity_sale !== null && item.current_quantity_sale !== undefined) {
          current = item.current_quantity_sale
        }
        if (selected.type === 'purchase' && item.current_quantity_purchase !== null && item.current_quantity_purchase !== undefined) {
          current = item.current_quantity_purchase
        }
        current = this.normalizeQty(current)
        return Object.assign({}, item, {
          current_quantity: current,
          real_quantity: current,
        })
      })
    },
    getInputClass(item) {
      if (!this.hasChanged(item)) return ''
      return item.real_quantity > item.current_quantity ? 'border-success' : 'border-danger'
    },
    getDiffClass(item) {
      return item.real_quantity > item.current_quantity ? 'text-success' : 'text-danger'
    },
    async startCamera() {
      if (!('BarcodeDetector' in window)) {
        this.cameraSupported = false
        this.$bvToast.toast('Tu navegador no soporta el escáner nativo. Escribí el código manualmente.', {
          title: 'No disponible',
          variant: 'warning',
          solid: true
        })
        return
      }
      this.modal.camera = true
      await this.$nextTick()
      try {
        this.cameraStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        this.$refs.videoElement.srcObject = this.cameraStream
        this.cameraDetector = new window.BarcodeDetector({
          formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e']
        })
        this.cameraInterval = setInterval(this.detectBarcode, 400)
      } catch (e) {
        this.$bvToast.toast('No se pudo acceder a la cámara', { title: 'Error', variant: 'danger', solid: true })
        this.modal.camera = false
      }
    },
    async detectBarcode() {
      if (!this.$refs.videoElement || !this.cameraDetector) return
      if (this.$refs.videoElement.readyState < 2) return
      try {
        var barcodes = await this.cameraDetector.detect(this.$refs.videoElement)
        if (barcodes.length > 0) {
          this.barcode = barcodes[0].rawValue
          this.stopCamera()
          this.modal.camera = false
          this.searchProduct()
        }
      } catch (e) { /* ignorar frames no decodificables */ }
    },
    stopCamera() {
      clearInterval(this.cameraInterval)
      this.cameraInterval = null
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach(function(t) { t.stop() })
        this.cameraStream = null
      }
      this.cameraDetector = null
    },
    formatQty(val) {
      if (val === null || val === undefined) return '0'
      var n = this.normalizeQty(val)
      return isNaN(n) ? '0' : n.toString()
    },
    normalizeQty(val) {
      var n = parseFloat(val)
      if (isNaN(n)) return 0
      return parseFloat(n.toFixed(4))
    },
    async processAdjustments() {
      if (this.changedCount === 0) return

      var unitReference = this.selectedUnitMeasurement && this.selectedUnitMeasurement.reference
        ? ' en ' + this.selectedUnitMeasurement.reference
        : ''
      var confirm = await this.$bvModal.msgBoxConfirm('¿Desea procesar ' + this.changedCount + ' ajuste(s) de stock' + unitReference + '?', {
        title: 'Procesar ajustes',
        size: 'lg',
        buttonSize: 'lg',
        okVariant: 'success',
        okTitle: 'Procesar',
        cancelTitle: 'Cancelar',
        footerClass: 'p-3 stock-count-confirm-footer',
        bodyClass: 'stock-count-confirm-body',
        headerBgVariant: 'success',
        headerTextVariant: 'white',
        hideHeaderClose: false,
        centered: true
      })

      if (!confirm) return

      this.loading.process = true

      var adjustments = this.stockByDeposit
        .filter(item => this.hasChanged(item))
        .map(item => ({
          deposit_id: item.deposit.id,
          real_quantity: item.real_quantity,
          current_quantity: item.current_quantity,
        }))

      serviceAPI.procesarAjustesStock({
        product_id: this.product.id,
        unit_measurement_id: this.selectedUnitMeasurement ? this.selectedUnitMeasurement.id : null,
        adjustments: adjustments,
        edit_source: 'stockCount',
      })
        .then((response) => {
          this.lastResult.operations_created = response.data.operations_created
          this.loading.process = false
          this.modal.success = true
        })
        .catch((error) => {
          this.$bvToast.toast(Error.showError(error), {
            title: 'Error al procesar',
            variant: 'danger',
            solid: true
          })
          this.loading.process = false
        })
    },
  }
}
</script>

<style scoped>
.stock-count-page {
  min-height: 100vh;
  background: #f8f9fa;
  font-size: 1.03rem;
}
.stock-count-header {
  z-index: 100;
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}
.stock-count-header h5 {
  font-size: 1.18rem;
}
.stock-count-header small {
  font-size: 0.84rem;
}
.stock-count-page .card {
  border-radius: 0.7rem;
}
.stock-count-page .card-body {
  padding: 1.05rem;
}
.stock-count-page .btn-lg {
  padding: 0.8rem 1.15rem;
  font-size: 1.02rem;
}
.stock-count-page .btn-sm {
  padding: 0.42rem 0.8rem;
  font-size: 0.9rem;
}
.stock-count-page .form-control,
.stock-count-page .custom-select,
.stock-count-page .input-group-text {
  min-height: 2.8rem;
  font-size: 1rem;
}
.stock-count-page .form-control-lg {
  min-height: 3.35rem;
  font-size: 1.02rem;
}
.stock-count-page .input-group > .input-group-prepend > .input-group-text,
.stock-count-page .input-group > .input-group-append > .input-group-text,
.stock-count-page .input-group > .input-group-append > .btn,
.stock-count-page .input-group > .input-group-prepend > .btn {
  min-height: 2.8rem;
}
.stock-count-page h6 {
  font-size: 0.98rem;
}
.stock-count-page .small {
  font-size: 0.88rem;
}
.stock-count-empty-state {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  border: 1px solid #b8daff;
  border-left: 0.35rem solid #20a8d8;
  border-radius: 0.65rem;
  background: #f4fbff;
  color: #31495f;
  box-shadow: 0 0.125rem 0.35rem rgba(0, 0, 0, 0.06);
}
.stock-count-empty-icon {
  flex: 0 0 auto;
  font-size: 2rem;
  color: #20a8d8;
}
.stock-count-empty-title {
  font-size: 1.12rem;
  font-weight: 700;
  line-height: 1.25;
}
.stock-count-empty-text {
  margin-top: 0.25rem;
  font-size: 0.98rem;
  line-height: 1.35;
  color: #617586;
}
.stock-count-total-card {
  border: 1px solid #c5b8e8;
  border-left: 0.35rem solid #6f42c1;
  border-radius: 0.7rem;
  background: #faf8ff;
  box-shadow: 0 0.15rem 0.45rem rgba(0, 0, 0, 0.08);
}
.stock-count-total-title {
  color: #39285a;
  font-size: 1.08rem;
  font-weight: 700;
  line-height: 1.2;
}
.stock-count-total-subtitle {
  color: #706483;
  font-size: 0.88rem;
  margin-top: 0.2rem;
}
.stock-count-total-unit {
  color: #39285a;
  font-size: 0.86rem;
  padding: 0.35rem 0.55rem;
  border: 1px solid #ded5f4;
}
.stock-count-total-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
.stock-count-total-box {
  padding: 0.85rem;
  border: 1px solid #e2dbf2;
  border-radius: 0.55rem;
  background: #fff;
}
.stock-count-total-box-primary {
  border-color: #cfc1ee;
  background: #f3effc;
}
.stock-count-total-label {
  color: #756985;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}
.stock-count-total-value {
  color: #2e2148;
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1.15;
  margin-top: 0.2rem;
}
.stock-count-total-diff {
  margin-top: 0.8rem;
  font-size: 0.98rem;
  font-weight: 700;
}
.stock-count-confirm-body {
  font-size: 1.25rem;
  padding: 2rem;
}
.stock-count-confirm-footer .btn {
  min-width: 9rem;
  padding: 0.85rem 1.5rem;
  font-size: 1.1rem;
}
</style>

<style>
body.stock-count-focus-mode .app-header,
body.stock-count-focus-mode .sidebar,
body.stock-count-focus-mode .aside-menu,
body.stock-count-focus-mode .app-footer,
body.stock-count-focus-mode .alert-clear-cache,
body.stock-count-focus-mode .shortcuts-dropdown-full {
  display: none !important;
}

body.stock-count-focus-mode .app-body {
  margin-top: 0 !important;
}

body.stock-count-focus-mode .main {
  margin-left: 0 !important;
  width: 100% !important;
}

body.stock-count-focus-mode .main .container-fluid {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

body.stock-count-focus-mode .tabs-layout > .small-tabs,
body.stock-count-focus-mode .tabs-layout > .nav.nav-tabs {
  display: none !important;
}

body.stock-count-focus-mode .tabs-layout > .p-0.pt-1 {
  padding-top: 0 !important;
}
</style>
